import { Action } from 'store/store';
import {
    Layout,
    LayoutState,
    LayoutStatus,
    LayoutErrorState,
} from 'layout/LayoutState';

export enum LayoutActionType {
    LAYOUT_RESET = 'LAYOUT_RESET',
    LAYOUT_STATE_CHANGE = 'LAYOUT_STATE_CHANGE',
    LAYOUT_SAVED = 'LAYOUT_SAVED',
    LAYOUT_SET_ACTIVE_INPUT = 'LAYOUT_SET_ACTIVE_INPUT',
    LAYOUT_SET_PANEL_ID = 'LAYOUT_SET_PANEL_ID',
    LAYOUT_CLEAR_PENDING_LAYOUT = 'LAYOUT_CLEAR_PENDING_LAYOUT',
    LAYOUT_SET_MANUAL_SAVE = 'LAYOUT_SET_MANUAL_SAVE',
    LAYOUT_SET_DIRTY = 'LAYOUT_SET_DIRTY',
}

export enum LayoutStateChangeSubtype {
    LOAD = 'LOAD',
    UPDATE = 'UPDATE',
}

export interface LayoutActionStateChangePayload {
    subType: LayoutStateChangeSubtype;
    layoutState: LayoutState;
}

export type LayoutActionStateChange = Action<
    LayoutActionType.LAYOUT_STATE_CHANGE,
    LayoutActionStateChangePayload
>;

export const layoutStateChangeAction: (
    subType: LayoutStateChangeSubtype,
    layoutState: LayoutState
) => LayoutActionStateChange = (subType, layoutState) => ({
    type: LayoutActionType.LAYOUT_STATE_CHANGE,
    payload: { subType, layoutState },
});

export const layoutStateLoadPendingAction = (
    layoutName: string,
    panelId?: string
) =>
    layoutStateChangeAction(LayoutStateChangeSubtype.LOAD, {
        status: LayoutStatus.PENDING,
        pendingLayout: {
            name: layoutName,
            panelId,
        },
    });
export const layoutStateLoadSuccessAction = (layout: Layout) =>
    layoutStateChangeAction(LayoutStateChangeSubtype.LOAD, {
        status: LayoutStatus.SUCCESS,
        result: layout,
    });

export const layoutStateLoadErrorAction = (error: LayoutErrorState) =>
    layoutStateChangeAction(LayoutStateChangeSubtype.LOAD, {
        status: LayoutStatus.ERROR,
        error,
    });

export const layoutStateUpdateAction = (
    layout: Layout
): LayoutActionStateChange =>
    layoutStateChangeAction(LayoutStateChangeSubtype.UPDATE, {
        status: LayoutStatus.SUCCESS,
        result: layout,
    });

export type LayoutSavedAction = Action<
    LayoutActionType.LAYOUT_SAVED,
    { layoutName: string; id: number; origin: string }
>;

export const layoutSavedAction: (
    layoutName: string,
    id: number,
    origin: string
) => LayoutSavedAction = (layoutName, id, origin) => ({
    type: LayoutActionType.LAYOUT_SAVED,
    payload: { layoutName, id, origin },
});

export interface LayoutActionSetActiveInputPayload {
    activeInputId: string | undefined;
}

export type LayoutActionSetActiveInput = Action<
    LayoutActionType.LAYOUT_SET_ACTIVE_INPUT,
    LayoutActionSetActiveInputPayload
>;

export const layoutSetActiveInputAction: (
    activeInputId: string | undefined
) => LayoutActionSetActiveInput = (activeInputId: string | undefined) => ({
    type: LayoutActionType.LAYOUT_SET_ACTIVE_INPUT,
    payload: { activeInputId },
});

export type LayoutSetPanelIdAction = Action<
    LayoutActionType.LAYOUT_SET_PANEL_ID,
    string
>;

export const layoutSetPanelIdAction: (
    panelId: string
) => LayoutSetPanelIdAction = (panelId) => ({
    type: LayoutActionType.LAYOUT_SET_PANEL_ID,
    payload: panelId,
});

export type ClearPendingLayoutAction =
    Action<LayoutActionType.LAYOUT_CLEAR_PENDING_LAYOUT>;

export const layoutClearPendingLayoutAction: () => ClearPendingLayoutAction =
    () => ({
        type: LayoutActionType.LAYOUT_CLEAR_PENDING_LAYOUT,
    });

export type SetLayoutManualSaveAction = Action<
    LayoutActionType.LAYOUT_SET_MANUAL_SAVE,
    boolean
>;

export const setLayoutManualSave: (
    manualSave: boolean
) => SetLayoutManualSaveAction = (manualSave) => ({
    type: LayoutActionType.LAYOUT_SET_MANUAL_SAVE,
    payload: manualSave,
});

export type SetLayoutDirtyAction = Action<
    LayoutActionType.LAYOUT_SET_DIRTY,
    boolean
>;

export const setLayoutDirty: (dirty: boolean) => SetLayoutDirtyAction = (
    dirty
) => ({
    type: LayoutActionType.LAYOUT_SET_DIRTY,
    payload: dirty,
});

export type ResetLayoutStateAction = Action<LayoutActionType.LAYOUT_RESET>;

export const resetLayoutAction: () => ResetLayoutStateAction = () => ({
    type: LayoutActionType.LAYOUT_RESET,
});

export type LayoutAction =
    | LayoutActionStateChange
    | LayoutSavedAction
    | LayoutActionSetActiveInput
    | LayoutSetPanelIdAction
    | ClearPendingLayoutAction
    | SetLayoutManualSaveAction
    | SetLayoutDirtyAction
    | ResetLayoutStateAction;
